import { useMemo } from "react";
import _ from "lodash";

import { Audience, SubCollection } from "@/migrated-widgets/table-map/services/MapDataLayer/types/Engage.types";

interface IAudienceGroupItem extends SubCollection {
    id: number;
    name: string;
    expansible?: boolean;
    visitorsCount: number;
    children: null | IAudienceGroupItem[];
    data: SubCollection["data"];
    type?: "audience" | "make" | "model";
    zipCodes: string[];
}

function useAudienceGroups(audiences: Audience[]) {
  const calculateTotals = (collection: SubCollection[]) => {
      const totals = collection.reduce((acc, crr) => acc + crr.data.visitorsCount, 0);

      return totals;
  };

  const getModels = (collection: SubCollection[]): IAudienceGroupItem[] | null => {
      const modelIds = _.compact(collection.map((item) => item.data.audienceVisitorReportModelId));

      if (!modelIds.length) return null;

      const models = _.groupBy(collection, (item) => item.data.audienceVisitorReportModelId);

      return Object.keys(models)
          .map((key): IAudienceGroupItem => {
              const _models = models[key];
              const model = models[key][0];

              return {
                  id: model.data.audienceVisitorReportModelId,
                  name: model.data.modelName,
                  data: model.data,
                  visitorsCount: calculateTotals(_models),
                  type: "model",
                  zipCodes: _models.map((item) => item.data.audienceVisitorReportZipCode),
                  children: null,
              };
          })
          .sort((a, b) => b.visitorsCount - a.visitorsCount);
  };

  const getMakes = (collection: SubCollection[]): IAudienceGroupItem[] | null => {
      const makeIds = _.compact(collection.map((item) => item.data.audienceVisitorReportMakeId));

      if (!makeIds.length) return null;

      const makes = _.groupBy(
          collection.filter((item) => item.data.audienceVisitorReportMakeId),
          (item) => item.data.audienceVisitorReportMakeId,
      );

      return Object.keys(makes)
          .map((key): IAudienceGroupItem => {
              const _makes = makes[key];
              const make = makes[key][0];

              const visitorsCount = calculateTotals(_makes);
              return {
                  id: make.data.audienceVisitorReportMakeId,
                  name: make.data.makeName,
                  data: make.data,
                  visitorsCount,
                  type: "make",
                  zipCodes: _makes.map((item) => item.data.audienceVisitorReportZipCode),
                  children: make.data.expandible ? getModels(makes[key]) : null,
              };
          })
          .sort((a, b) => b.visitorsCount - a.visitorsCount);
  };

  const getAudiences = (collection: SubCollection[], nest: boolean = false): IAudienceGroupItem[] => {
      const groups = _.groupBy(collection, (item) => item.data.audienceId);

      return Object.keys(groups).map((key) => {
          const audiences = groups[key] ?? [];
          const audience = audiences[0];
          const visitorsCount = calculateTotals(audiences);

          return {
              id: audience.data.audienceId,
              name: audience.data.audienceName,
              data: audience.data,
              visitorsCount,
              type: "audience",
              zipCodes: audiences.map((item) => item.data.audienceVisitorReportZipCode),
              children: nest && audience.data.expandible ? getMakes(groups[key]) : null,
          };
      });
  };

  const groups = useMemo((): IAudienceGroupItem[] => {
      const _audiences = audiences.flatMap((audience) => audience.dataCollection).filter((audience) => audience.data.audienceId && audience.data.groupId);

      // * NEW MODEL AUDIENCES
      const newModelAudiences = _.compact(_audiences.filter((_audience) => _audience.data.audienceName.toLowerCase().includes("<model>")));

      // * All Audiences (create groups)
      const audienceGroups = _.groupBy(_audiences, (audience) => audience.data.groupId);

      const data = Object.keys(audienceGroups).map((key) => {
          const audiences = audienceGroups[key] ?? [];
          const audience = audiences[0];
          const visitorsCount = calculateTotals(audiences);
          return {
              id: audience.data.groupId,
              name: audience.data.groupName,
              expansible: Boolean(audience.data.expandible),
              visitorsCount,
              zipCodes: audiences.map((item) => item.data.audienceVisitorReportZipCode),
              children: [...(getAudiences(audienceGroups[key]) ?? [])],
              data: audience.data,
          };
      });

      const newGroupIndex = data.findIndex((_group) => _group.name.toLowerCase() === "new");

      // * ADDING AUDIENCES TO NEW MODEL GROUP
      if (newGroupIndex !== -1) {
          data[newGroupIndex] = {
              ...data[newGroupIndex],
              children: [...(getMakes(newModelAudiences) ?? []), ...getNewModelAudiences(data[newGroupIndex].children ?? [])],
          };
      }

      // * Calculate totals for each row
      return data
          .map((item) => ({
              ...item,
              visitorsCount: !item.children.length ? item.visitorsCount : item.children?.reduce((acc, crr) => acc + (crr?.visitorsCount ?? 0), 0),
          }))
          .sort((a, b) => b.visitorsCount - a.visitorsCount);
  }, [audiences]);

  return groups;
}

function getNewModelAudiences(audiences: IAudienceGroupItem[]) {
    // * Prevent New Model Audience show up in New Group
    const newModelAudiencesFiltered = audiences.filter((_audience) => !_audience.data.audienceName.toLowerCase().includes("<model>"));

    return newModelAudiencesFiltered;
}

export { useAudienceGroups, type IAudienceGroupItem };
